import React, { useState, useRef, forwardRef } from "react";
import { Box, Typography } from "@mui/material";
import { IExpertise } from "./constants";
import { SingleInput } from "./SingleInput";
import { SingleLineEntry } from "./singleLineEntry";
import * as Reorder from "../../../../components/dnd/Reorder";
import { useMergePendingItems, useMergePendingItemsHandle } from "../../../../hooks/useMergePendingItems";

interface Props {
  expertise: Array<IExpertise>;
  updateExpertise: (keyPoints: Array<IExpertise>) => void;
}

export default forwardRef<{ mergePendingItems: () => IExpertise[] }, Props>(
  function FormExpertise({ expertise, updateExpertise }, ref) {
    const [editableIndex, setEditableIndex] = useState(-1);
    const inputRef = useRef<{ getCurrentValue: () => string; clear: () => void }>(null);

    const { pendingItems, mergePendingItems, addToPending, clearPending } = useMergePendingItems<IExpertise, string>({
      items: expertise,
      updateItems: updateExpertise,
      inputRef,
      transformItem: (item) => ({ expertise: item as string }),
      mergeOrder: 'prepend',
    });

    useMergePendingItemsHandle(ref, mergePendingItems);

    const isValidKeyPoint = (text: string) => text?.trim()?.length;

    const handleAddToPending = (skill: string) => {
      if (isValidKeyPoint(skill)) {
        addToPending(skill);
      }
    };

    const addSingleInterest = (skill: string) => {
      if (isValidKeyPoint(skill)) {
        const allItems = [...pendingItems, skill];
        updateExpertise([...allItems.map(item => ({ expertise: item })), ...expertise]);
        clearPending();
      }
      setEditableIndex(-1);
    };

  const updateSingleInterest = (skill: string, index: number) => {
    const tempList = [...expertise];
    tempList[index].expertise = skill;
    updateExpertise([...tempList]);
    setEditableIndex(-1);
  };

  const removeSingleInterest = (index: number) => {
    const tempList = [...expertise];
    tempList.splice(index, 1);
    updateExpertise([...tempList]);
  };

  const props = { label: "Expertise" };
  return (
    <Box className="flex flex-col gap-2 w-full">
      <SingleInput 
        ref={inputRef}
        {...props} 
        onTextChange={addSingleInterest}
        onAddToPending={handleAddToPending}
      />

      {pendingItems.length > 0 && (
        <Box className="flex flex-col gap-2 w-full mb-2">
          {pendingItems.map((item, index) => (
            <Box key={`pending-${index}`} className="p-2 border border-dashed border-border-token rounded-lg bg-surface-2">
              <Typography className="text-text-1">{item}</Typography>
            </Box>
          ))}
        </Box>
      )}

      <Reorder.Root options={expertise} onUpdateOrder={updateExpertise}>
        {expertise.map((item, index) => {
          if (editableIndex === index) {
            return (
              <SingleInput
                {...props}
                key={`interest-${item}-${index}`}
                text={item.expertise}
                onTextChange={(text) => updateSingleInterest(text, index)}
                isEditMode={true}
              />
            );
          }

          return (
            <SingleLineEntry
              text={item.expertise}
              index={index}
              key={`interest-${item}-${index}`}
              editText={() => setEditableIndex(index)}
              removeText={() => removeSingleInterest(index)}
            />
          );
        })}
      </Reorder.Root>
    </Box>
  );
});
